/*

      =========================================================
      * XGuard
      =========================================================
      
      * Product Page: https://www.nelx.net/xguard
      * Copyright 2024 NELX (https://www.nelx.net/)
      
      =========================================================
*/

import React, { useEffect, useState } from "react";
import logo from "assets/img/logo.png";
import axios from "axios";
import Cookies from "js-cookie";
import { keyframes } from "@chakra-ui/react";
import config from "config";
import useCustomToast from "components/Toast/useCustomToast";
import { Link as RouterLink, useHistory, Redirect } from "react-router-dom";

// Chakra imports
import {
  Box,
  Flex,
  Button,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Link,
  Switch,
  Text,
  DarkMode,
} from "@chakra-ui/react";

// Assets
import signInImage from "assets/img/signInImage.png";

// Custom Components
import GradientBorder from "components/GradientBorder/GradientBorder";

// Animations
const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

const glow = keyframes`
  0%, 100% { box-shadow: 0 0 20px rgba(139, 92, 246, 0.3); }
  50% { box-shadow: 0 0 40px rgba(139, 92, 246, 0.6); }
`;

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

function SignIn() {
  const titleColor = "white";
  const textColor = "gray.400";
  const { success, error } = useCustomToast();
  const history = useHistory();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // If token is already set, redirect to dashboard
  useEffect(() => {
    const checkToken = async () => {
      try {
        const token = Cookies.get("token");
        if(token === undefined) {
          return;
        }
        // TODO : replace using APISERVICE
        const response = await axios.get(`${config.apiUrl}/checkToken`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        if (response.status === 200) {
          setIsAuthenticated(true);
          window.location.href = "/";
        }
      } catch (error) {
        return;
      }
    };

    checkToken();
  }, [Cookies.get("token")]);

  // Handle login
  const handleLogin = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    try {

      // Check if password and username are not empty
      if (username === "" || password === "") {
        error("Oops", "Veuillez remplir tous les champs");
        setIsLoading(false);
        return;
      }

      // set token expiration
      const token_expiration = rememberMe ? 365 : 1;
      // TODO : replace using APISERVICE
      const response = await axios.post(config.apiUrl + "/login", {
        username,
        password,
        token_expiration,
      });

      // Set token in cookies
      const { token } = response.data;
      Cookies.set("token", token, {
        expires: token_expiration,
        sameSite: "none",
        secure: true,
      });

      // Show success message
      success("Connexion réussie", "Bienvenue sur " + config.appName + " !");

      // Redirect to dashboard
      setIsAuthenticated(true);
      window.location.href = "/";

    } catch (error) {
      setIsLoading(false);
      if (error.response && error.response.status === 400) {
        // Affiche un message d'erreur spécifique pour les erreurs 400
        error("Oops", error.response.data.message || "Vos identifiants sont incorrects");
      } else if (error.code === "ERR_NETWORK") {
        // Affiche un message d'erreur spécifique pour les erreurs réseau
        error("Oops", "Impossible de se connecter au serveur");
      } else {
        // Affiche un message d'erreur général pour les autres types d'erreurs
        error("Oops", "Vos identifiants sont incorrects");
      }
    }    
  };

  if(isAuthenticated) {
    return <Redirect to="/admin" />;
  }

  return (
    <form onSubmit={handleLogin}>
      {/* Animated background effects */}
      <Box
        position="fixed"
        top="0"
        left="0"
        right="0"
        bottom="0"
        overflow="hidden"
        zIndex="-1"
        bg="linear-gradient(135deg, #0F123B 0%, #090D2E 50%, #020515 100%)"
      >
        {/* Floating orbs for visual effect */}
        <Box
          position="absolute"
          top="20%"
          left="10%"
          width="300px"
          height="300px"
          borderRadius="50%"
          bg="radial-gradient(circle, rgba(139, 92, 246, 0.15) 0%, transparent 70%)"
          filter="blur(40px)"
          animation={`${float} 6s ease-in-out infinite`}
        />
        <Box
          position="absolute"
          bottom="20%"
          right="10%"
          width="400px"
          height="400px"
          borderRadius="50%"
          bg="radial-gradient(circle, rgba(6, 182, 212, 0.1) 0%, transparent 70%)"
          filter="blur(60px)"
          animation={`${float} 8s ease-in-out infinite`}
          animationDelay="2s"
        />
        <Box
          position="absolute"
          top="60%"
          left="60%"
          width="200px"
          height="200px"
          borderRadius="50%"
          bg="radial-gradient(circle, rgba(168, 85, 247, 0.12) 0%, transparent 70%)"
          filter="blur(50px)"
          animation={`${float} 7s ease-in-out infinite`}
          animationDelay="1s"
        />
      </Box>

      <Flex position='relative'>
        <Flex
          minH='100vh'
          h={{ base: "120vh", lg: "fit-content" }}
          w='100%'
          mx='auto'
          me={{ base: "auto", lg: "50px", xl: "auto" }}>
          <GradientBorder
            mt={{ base: "100px"}} 
            style={{
              position: "absolute",
              left: "50%",
              transform: "translate(-50%, 0%)"
            }}
            animation={`${glow} 3s ease-in-out infinite`}
            borderRadius="30px"
          >
            <Flex
              background='transparent'
              borderRadius='30px'
              direction='column'
              p='40px'
              minW={{ base: "unset", md: "430px", xl: "450px" }}
              w='100%'
              mx={{ base: "0px" }}
              bg={{
                base: "rgba(19,21,56, 0.95)",
              }}
              backdropFilter="blur(20px)"
              boxShadow="0 25px 50px -12px rgba(0, 0, 0, 0.5)"
            >
              <Flex
                style={{ userSelect: "none" }}
                position='relative'
                left='50%'
                transform='translate(-50%, 0%)'
                px='50px'>
                <Flex
                  direction='column'
                  w='100%'
                  alignItems='center'
                  background='transparent'>
                  {/* Animated logo */}
                  <Box 
                    animation={`${float} 4s ease-in-out infinite`}
                    mb="10px"
                  >
                    <img src={logo} width='150px' alt="XGuard Logo" style={{ filter: "drop-shadow(0 0 20px rgba(139, 92, 246, 0.5))" }} />
                  </Box>
                  <Heading 
                    color={titleColor} 
                    fontSize='32px' 
                    mb='10px'
                    animation={`${fadeInUp} 0.6s ease-out`}
                    bgGradient="linear(to-r, white, gray.300)"
                    bgClip="text"
                  >
                    {config.appName}
                  </Heading>
                  <Text
                    mb='36px'
                    ms='4px'
                    color={textColor}
                    fontWeight='bold'
                    fontSize='14px'
                    animation={`${fadeInUp} 0.6s ease-out 0.1s both`}
                  >
                    Entrez vos identifiants pour vous connecter
                  </Text>

                  {/* Identifiant */}
                  <FormControl 
                    alignContent='center' 
                    w='100%' 
                    maxW='350px'
                    animation={`${fadeInUp} 0.6s ease-out 0.2s both`}
                  >
                    <FormLabel
                      ms='4px'
                      fontSize='sm'
                      fontWeight='normal'
                      color='white'>
                      Identifiant
                    </FormLabel>
                    <GradientBorder
                      mb='24px'
                      w={{ base: "100%", lg: "fit-content" }}
                      borderRadius='20px'>
                      <Input
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        color='white'
                        bg='rgb(19,21,54)'
                        border='transparent'
                        borderRadius='20px'
                        fontSize='sm'
                        size='lg'
                        w={{ base: "100%", md: "346px" }}
                        maxW='100%'
                        h='46px'
                        placeholder='Votre identifiant'
                        transition="all 0.2s ease"
                        _focus={{
                          bg: 'rgb(25,28,70)',
                          boxShadow: '0 0 0 2px rgba(139, 92, 246, 0.3)',
                        }}
                      />
                    </GradientBorder>
                  </FormControl>

                  {/* Mot de passe */}
                  <FormControl 
                    alignContent='center' 
                    w='100%' 
                    maxW='350px'
                    animation={`${fadeInUp} 0.6s ease-out 0.3s both`}
                  >
                    <FormLabel
                      type='password'
                      ms='4px'
                      fontSize='sm'
                      fontWeight='normal'
                      color='white'>
                      Mot de passe
                    </FormLabel>
                    <GradientBorder
                      mb='24px'
                      w={{ base: "100%", lg: "fit-content" }}
                      borderRadius='20px'>
                      <Input
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        color='white'
                        bg='rgb(19,21,54)'
                        border='transparent'
                        borderRadius='20px'
                        fontSize='sm'
                        size='lg'
                        w={{ base: "100%", md: "346px" }}
                        maxW='100%'
                        type='password'
                        placeholder='Votre mot de passe'
                        transition="all 0.2s ease"
                        _focus={{
                          bg: 'rgb(25,28,70)',
                          boxShadow: '0 0 0 2px rgba(139, 92, 246, 0.3)',
                        }}
                      />
                    </GradientBorder>
                  </FormControl>

                  {/* Se souvenir de moi */}
                  <FormControl 
                    display='flex' 
                    alignContent='center' 
                    w='100%' 
                    maxW='350px'
                    animation={`${fadeInUp} 0.6s ease-out 0.4s both`}
                  >
                    <DarkMode>
                      <Switch
                        id='remember-login'
                        colorScheme='purple'
                        me='10px'
                        isChecked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)} />
                    </DarkMode>
                    <FormLabel
                      htmlFor='remember-login'
                      mb='0'
                      ms='1'
                      fontWeight='normal'
                      color='white'>
                      Se souvenir de moi
                    </FormLabel>
                  </FormControl>

                  {/* Bouton de connexion */}
                  <Button
                    variant='brand'
                    fontSize='16px'
                    type='submit'
                    w='100%'
                    maxW='350px'
                    h='45'
                    mb='20px'
                    mt='20px'
                    isLoading={isLoading}
                    loadingText="Connexion..."
                    animation={`${fadeInUp} 0.6s ease-out 0.5s both`}
                    _hover={{
                      transform: 'translateY(-2px)',
                      boxShadow: '0 10px 40px rgba(139, 92, 246, 0.5)',
                    }}
                  >
                    CONNEXION
                  </Button>

                  {/* Lien vers la page d'enregistrement' */}
                  <Flex
                    flexDirection='column'
                    justifyContent='center'
                    alignItems='center'
                    maxW='100%'
                    mt='0px'
                    animation={`${fadeInUp} 0.6s ease-out 0.6s both`}
                  >
                    <Text color={textColor} fontWeight='medium'>
                      Vous n'avez pas de compte ?
                      <Link 
                        as={RouterLink} 
                        to="/auth/signup" 
                        color={titleColor} 
                        ms='5px' 
                        fontWeight='bold'
                        transition="all 0.2s ease"
                        _hover={{
                          color: '#a855f7',
                          textDecoration: 'none',
                        }}
                      >
                        Demander un compte
                      </Link>
                    </Text>
                  </Flex>
                </Flex>
              </Flex>
            </Flex>
          </GradientBorder>
        </Flex>
      </Flex>
    </form>

  );
}

export default SignIn;
